import { useState, useCallback, useRef } from "react";
import { Loader2, ScanLine, Upload, BookCheck, RefreshCw, FileText, Truck, AlertTriangle, Package } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { runScanJob } from "../../lib/scanJobs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import KiBuchungssatzResult from "./KiBuchungssatzResult";

const eur = (n) => `${(Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Juli-2026 (User-Wunsch) — KI-Beleg-Scan & Kontierung.
 * Beleg hochladen → Gemini-Vision liest aus → Skonto + Kontierung + WMS-
 * Lieferschein-/Bestell-Abgleich → nach Bestätigung Kreditor + Buchungsstapel.
 */
export default function BelegScanTab() {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [kontenrahmen, setKontenrahmen] = useState("SKR03");
    const [scanning, setScanning] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [rebooking, setRebooking] = useState(false);
    const [scan, setScan] = useState(null); // full response
    const [form, setForm] = useState(null); // editable fields
    const [satz, setSatz] = useState(null);
    const [selectedDn, setSelectedDn] = useState("");

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const reset = () => {
        setScan(null); setForm(null); setSatz(null); setFile(null); setSelectedDn("");
        if (fileRef.current) fileRef.current.value = "";
    };

    const doScan = useCallback(async () => {
        if (!file) { toast.error("Bitte zuerst einen Beleg (PDF/Bild/XML) auswählen"); return; }
        // Jul-2026 (User-Wunsch, Vereinheitlichung): E-Rechnungen (XRechnung/
        // ZUGFeRD-XML) laufen über denselben Upload — XML wird strukturiert
        // importiert (ohne KI) und landet direkt in den Kreditoren.
        if (/\.xml$/i.test(file.name || "")) {
            setScanning(true);
            try {
                const fd = new FormData();
                fd.append("file", file);
                const r = await api.post("/buchhaltung/kreditoren/e-rechnung-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                const p = r.data.parsed || {};
                toast.success(`E-Rechnung importiert: ${p.invoice_number || ""} · ${eur(p.amount_gross)} (${p.format})${r.data.supplier_auto_created ? " · Lieferant neu angelegt" : ""} — siehe Tab „Kreditoren“`);
                reset();
            } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "E-Rechnung-Import fehlgeschlagen");
            } finally { setScanning(false); }
            return;
        }
        setScanning(true); setScan(null); setForm(null); setSatz(null); setSelectedDn("");
        try {
            // Jul-2026 (User-Wunsch „maximale Scan-Power") — Hintergrund-Job statt
            // langem HTTP-Request (Cloudflare-100s-Limit, blockierungsfrei).
            const d = await runScanJob("beleg", file, { params: { kontenrahmen } });
            setScan(d);
            const p = d.parsed || {};
            setForm({
                supplier_name: d.supplier?.name || p.supplier_name || "",
                supplier_id: d.supplier?.id || null,
                supplier_vat_id: p.supplier_vat_id || d.supplier?.vat_id || "",
                supplier_iban: p.supplier_iban || d.supplier?.iban || "",
                invoice_number: p.invoice_number || "",
                invoice_date: (p.invoice_date || "").slice(0, 10),
                due_date: (p.due_date || "").slice(0, 10),
                amount_gross: p.amount_gross ?? "",
                amount_net: p.amount_net ?? "",
                vat_rate: p.vat_rate ?? 19,
                vat_amount: p.vat_amount ?? "",
                skonto_percent: p.skonto_percent ?? d.supplier?.skonto_percent ?? "",
                skonto_days: p.skonto_days ?? d.supplier?.skonto_days ?? "",
                delivery_note_number: p.delivery_note_number || "",
            });
            setSatz(d.buchungssatz || null);
            if ((d.delivery_note_matches || []).length) setSelectedDn(d.delivery_note_matches[0].id);
            if (d.parsed?.mocked) toast.warning("KI-Auslesung nicht verfügbar — bitte Felder manuell ergänzen.");
            else toast.success("Beleg ausgelesen — bitte prüfen und übernehmen.");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Beleg-Scan fehlgeschlagen");
        } finally {
            setScanning(false);
        }
    }, [file, kontenrahmen]);

    const rebook = async () => {
        if (!form?.amount_gross || Number(form.amount_gross) <= 0) { toast.error("Bruttobetrag fehlt"); return; }
        setRebooking(true);
        try {
            const r = await api.post("/buchhaltung/ki-buchungssatz", {
                buchungstext: [form.supplier_name, form.invoice_number ? `Rechnung ${form.invoice_number}` : ""].filter(Boolean).join(" · ") || "Eingangsrechnung",
                betrag_brutto: Number(form.amount_gross),
                ust_satz: Number(form.vat_rate) || 19,
                richtung: "ausgabe",
                kontenrahmen,
                geschaeftspartner: form.supplier_name || null,
                zahlungsart: "offen",
                source_type: "beleg-scan",
            });
            setSatz(r.data);
            toast.success("Buchungssatz neu vorgeschlagen");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Vorschlag fehlgeschlagen");
        } finally {
            setRebooking(false);
        }
    };

    const commit = async () => {
        if (!form?.supplier_name?.trim()) { toast.error("Lieferantenname erforderlich"); return; }
        if (!form?.amount_gross || Number(form.amount_gross) <= 0) { toast.error("Bruttobetrag erforderlich"); return; }
        setCommitting(true);
        try {
            const dn = (scan?.delivery_note_matches || []).find((m) => m.id === selectedDn);
            const payload = {
                supplier: { id: form.supplier_id || null, name: form.supplier_name.trim(), vat_id: form.supplier_vat_id || null, iban: form.supplier_iban || null },
                invoice_number: form.invoice_number || null,
                invoice_date: form.invoice_date || null,
                due_date: form.due_date || null,
                amount_gross: Number(form.amount_gross),
                amount_net: form.amount_net === "" ? null : Number(form.amount_net),
                vat_rate: Number(form.vat_rate) || 0,
                vat_amount: form.vat_amount === "" ? null : Number(form.vat_amount),
                skonto: { percent: form.skonto_percent === "" ? null : Number(form.skonto_percent), days: form.skonto_days === "" ? null : Number(form.skonto_days) },
                delivery_note_number: (dn?.delivery_note_number) || form.delivery_note_number || null,
                delivery_note_scan_id: dn?.id || null,
                purchase_order_id: scan?.purchase_order_match?.id || null,
                buchungssatz: satz || null,
                archive: scan?.archive || null,
                add_to_buchungsstapel: true,
            };
            const r = await api.post("/buchhaltung/beleg-scan/commit", payload);
            toast.success(`Kreditor angelegt${r.data?.buchungsstapel_id ? " + in Buchungsstapel übernommen" : ""}`);
            reset();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Übernehmen fehlgeschlagen");
        } finally {
            setCommitting(false);
        }
    };

    const p = scan?.parsed || {};
    const originalUrl = scan?.archive?.path ? `${API}/api/files/${scan.archive.path}` : null;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="beleg-scan-tab">
            {/* Upload / Eingabe */}
            <div className="lg:col-span-2 space-y-4">
                <div className="bg-white border border-gray-200 rounded-sm p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <ScanLine className="w-5 h-5 text-[#111827]" />
                        <h2 className="font-display font-black text-lg">Beleg-Scan &amp; Kontierung</h2>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">Eingangsrechnung/Beleg hochladen — PDF/Bild liest die KI aus (Skonto, Lieferschein-Abgleich, Kontierung). XRechnung/ZUGFeRD-XML wird direkt strukturiert importiert.</p>

                    <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.xml" onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:text-sm file:font-bold file:bg-[#111827] file:text-[#FFCC00] mb-3" data-testid="beleg-scan-file" />

                    <div className="flex items-center gap-3 mb-3">
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kontenrahmen</label>
                            <select value={kontenrahmen} onChange={(e) => setKontenrahmen(e.target.value)} className="block h-10 rounded-sm border border-gray-300 px-2 text-sm mt-1" data-testid="beleg-scan-kr">
                                <option value="SKR03">SKR03</option>
                                <option value="SKR04">SKR04</option>
                                <option value="SKR51">SKR51 (Kfz)</option>
                            </select>
                        </div>
                        <Button onClick={doScan} disabled={scanning || !file} className="flex-1 self-end rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] font-black uppercase tracking-wider h-10" data-testid="beleg-scan-submit">
                            {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Liest aus…</> : <><Upload className="w-4 h-4 mr-2" /> Beleg auslesen</>}
                        </Button>
                    </div>
                    {originalUrl && (
                        <a href={originalUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#111827] underline" data-testid="beleg-scan-original">
                            <FileText className="w-3.5 h-3.5" /> Original ansehen
                        </a>
                    )}
                </div>

                {form && (
                    <div className="bg-white border border-gray-200 rounded-sm p-5 space-y-3" data-testid="beleg-scan-fields">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Belegdaten (prüfen &amp; anpassen)</div>
                        <Field label="Lieferant" v={form.supplier_name} on={(v) => set("supplier_name", v)} testid="bs-supplier" />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Rechnungsnr." v={form.invoice_number} on={(v) => set("invoice_number", v)} testid="bs-invno" />
                            <Field label="Lieferschein-Nr." v={form.delivery_note_number} on={(v) => set("delivery_note_number", v)} testid="bs-dn" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Rechnungsdatum" type="date" v={form.invoice_date} on={(v) => set("invoice_date", v)} testid="bs-invdate" />
                            <Field label="Fällig am" type="date" v={form.due_date} on={(v) => set("due_date", v)} testid="bs-due" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <Field label="Brutto €" type="number" v={form.amount_gross} on={(v) => set("amount_gross", v)} testid="bs-gross" />
                            <Field label="Netto €" type="number" v={form.amount_net} on={(v) => set("amount_net", v)} testid="bs-net" />
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">USt %</label>
                                <select value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} className="w-full h-10 rounded-sm border border-gray-300 px-2 text-sm mt-1" data-testid="bs-vat">
                                    <option value={19}>19</option><option value={7}>7</option><option value={0}>0</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-dashed border-gray-200">
                            <Field label="Skonto %" type="number" v={form.skonto_percent} on={(v) => set("skonto_percent", v)} testid="bs-skonto-pct" />
                            <Field label="Skonto Tage" type="number" v={form.skonto_days} on={(v) => set("skonto_days", v)} testid="bs-skonto-days" />
                        </div>
                        {Number(form.skonto_percent) > 0 && Number(form.amount_gross) > 0 && (
                            <div className="text-xs text-emerald-700 font-bold" data-testid="bs-skonto-hint">
                                Skonto erkannt: {eur(Number(form.amount_gross) * Number(form.skonto_percent) / 100)} bei Zahlung innerhalb {form.skonto_days || "—"} Tagen
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Ergebnisse */}
            <div className="lg:col-span-3 space-y-4">
                {!scan && (
                    <div className="bg-white border border-dashed border-gray-300 rounded-sm p-8 text-center text-gray-400" data-testid="beleg-scan-empty">
                        <ScanLine className="w-8 h-8 mx-auto mb-2" />
                        Lade links einen Beleg hoch — die KI erstellt einen Buchungsvorschlag und gleicht Lieferscheine ab.
                    </div>
                )}

                {scan?.duplicate && (
                    <div className="bg-red-50 border border-red-200 rounded-sm p-4 flex items-start gap-2 text-sm text-red-800" data-testid="beleg-scan-duplicate">
                        <AlertTriangle className="w-4 h-4 mt-0.5" />
                        <div>Achtung: Rechnung <b>{scan.duplicate.invoice_number}</b> ({eur(scan.duplicate.amount_gross)}) wurde bereits erfasst. Ein erneutes Übernehmen wird abgelehnt.</div>
                    </div>
                )}

                {scan && (scan.delivery_note_matches || []).length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="beleg-scan-dn-matches">
                        <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-500"><Truck className="w-4 h-4 text-[#111827]" /> Passende WMS-Lieferscheine</div>
                        <ul className="space-y-1.5">
                            {scan.delivery_note_matches.map((m) => (
                                <li key={m.id}>
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="radio" name="dn" checked={selectedDn === m.id} onChange={() => setSelectedDn(m.id)} data-testid={`bs-dn-${m.id}`} />
                                        <span className="font-mono-num font-bold text-[#111827]">{m.delivery_note_number || "—"}</span>
                                        <span className="text-gray-500">· {m.supplier_name || "—"}</span>
                                        {m.match_type === "number" && <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm">Nr. passt</span>}
                                    </label>
                                </li>
                            ))}
                            <li><label className="flex items-center gap-2 text-sm cursor-pointer text-gray-500">
                                <input type="radio" name="dn" checked={!selectedDn} onChange={() => setSelectedDn("")} data-testid="bs-dn-none" /> Kein Lieferschein zuordnen
                            </label></li>
                        </ul>
                    </div>
                )}

                {scan?.purchase_order_match && !scan.purchase_order_match.price_deviation && (
                    <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 flex items-center gap-2 text-sm text-sky-800" data-testid="beleg-scan-po-match">
                        <Package className="w-4 h-4" /> Bestellung <b>{scan.purchase_order_match.order_number}</b> ({eur(scan.purchase_order_match.total_gross)}) passt betraglich — wird verknüpft.
                    </div>
                )}
                {/* Jul-2026 (User-Wunsch): Preisabweichung bestellt vs. berechnet ROT markieren */}
                {scan?.purchase_order_match?.price_deviation && (
                    <div className="bg-red-50 border-2 border-red-400 rounded-sm p-3 text-sm text-red-800" data-testid="beleg-scan-po-deviation">
                        <div className="flex items-center gap-2 font-bold">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            Preisabweichung zur Bestellung {scan.purchase_order_match.order_number}!
                        </div>
                        <div className="mt-1 font-mono-num">
                            Bestellt: <b>{eur(scan.purchase_order_match.price_deviation.expected)}</b> · Berechnet: <b>{eur(scan.purchase_order_match.price_deviation.actual)}</b> · Differenz:{" "}
                            <b className="text-red-700">{scan.purchase_order_match.price_deviation.diff > 0 ? "+" : ""}{eur(scan.purchase_order_match.price_deviation.diff)} ({scan.purchase_order_match.price_deviation.percent > 0 ? "+" : ""}{scan.purchase_order_match.price_deviation.percent} %)</b>
                        </div>
                        <div className="mt-1 text-xs text-red-700">Bestellung wird trotzdem verknüpft — bitte Beträge prüfen (Nachlass? Fracht? Teillieferung?).</div>
                    </div>
                )}

                {form && (
                    <div className="flex items-center justify-between">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Kontierungs-Vorschlag</div>
                        <button onClick={rebook} disabled={rebooking} className="inline-flex items-center gap-1 text-xs font-bold text-gray-600 hover:text-[#111827] border border-gray-300 rounded-sm px-3 h-8 bg-white" data-testid="beleg-scan-rebook">
                            {rebooking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Neu vorschlagen ({kontenrahmen})
                        </button>
                    </div>
                )}
                {satz ? <KiBuchungssatzResult result={satz} /> : (form && (
                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm text-amber-800">Kein automatischer Buchungssatz — „Neu vorschlagen" nutzen oder ohne Kontierung übernehmen.</div>
                ))}

                {form && (
                    <button type="button" onClick={commit} disabled={committing || !!scan?.duplicate}
                        className="w-full h-12 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="beleg-scan-commit">
                        {committing ? <Loader2 className="w-5 h-5 animate-spin" /> : <><BookCheck className="w-5 h-5" /> Kreditor + Buchung übernehmen</>}
                    </button>
                )}
            </div>
        </div>
    );
}

function Field({ label, v, on, type = "text", testid }) {
    return (
        <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</label>
            <Input type={type} step={type === "number" ? "0.01" : undefined} value={v ?? ""} onChange={(e) => on(e.target.value)} className="rounded-sm mt-1" data-testid={testid} />
        </div>
    );
}
